import { atOrThrow } from "./helpers.js";
/** Order-based queue with block-aware FIFO and LIFO strategies. */
export class OrderedQueue {
    #dequeueOrder;
    #blockOrder;
    #fifoItems = [];
    #fifoHeadIndex = 0;
    #lifoItems = [];
    #size = 0;
    constructor(options) {
        if (options.dequeueOrder !== "fifo" && options.dequeueOrder !== "lifo") {
            throw new TypeError(`Invalid dequeueOrder: ${String(options.dequeueOrder)}`);
        }
        if (options.blockOrder !== "preserve" && options.blockOrder !== "reverse") {
            throw new TypeError(`Invalid blockOrder: ${String(options.blockOrder)}`);
        }
        this.#dequeueOrder = options.dequeueOrder;
        this.#blockOrder = options.blockOrder;
    }
    get size() {
        return this.#size;
    }
    isEmpty() {
        return this.#size === 0;
    }
    enqueue(...items) {
        this.#enqueueBlock(items);
    }
    enqueueMany(items) {
        this.#enqueueBlock(Array.from(items));
    }
    peek() {
        if (this.#size === 0)
            return undefined;
        return this.#dequeueOrder === "fifo"
            ? this.#fifoItems[this.#fifoHeadIndex]
            : this.#lifoItems[this.#lifoItems.length - 1];
    }
    peekOrThrow() {
        const item = this.peek();
        if (item === undefined)
            throw new Error("Cannot peek from an empty queue.");
        return item;
    }
    dequeue() {
        if (this.#size === 0)
            return undefined;
        this.#size -= 1;
        if (this.#dequeueOrder === "fifo") {
            const item = this.#fifoItems[this.#fifoHeadIndex++];
            if (this.#fifoHeadIndex > 1024 && this.#fifoHeadIndex * 2 >= this.#fifoItems.length) {
                this.#fifoItems = this.#fifoItems.slice(this.#fifoHeadIndex);
                this.#fifoHeadIndex = 0;
            }
            return item;
        }
        return this.#lifoItems.pop();
    }
    dequeueOrThrow() {
        const item = this.dequeue();
        if (item === undefined)
            throw new Error("Cannot dequeue from an empty queue.");
        return item;
    }
    clear() {
        this.#fifoItems = [];
        this.#fifoHeadIndex = 0;
        this.#lifoItems = [];
        this.#size = 0;
    }
    #enqueueBlock(items) {
        if (items.length === 0)
            return;
        this.#size += items.length;
        if (this.#dequeueOrder === "fifo")
            this.#appendFifoBlock(items);
        else
            this.#appendLifoBlock(items);
    }
    #appendFifoBlock(items) {
        if (this.#blockOrder === "preserve") {
            this.#fifoItems.push(...items);
            return;
        }
        for (let index = items.length - 1; index >= 0; index -= 1) {
            this.#fifoItems.push(atOrThrow(items, index));
        }
    }
    #appendLifoBlock(items) {
        if (this.#blockOrder === "preserve") {
            for (let index = items.length - 1; index >= 0; index -= 1) {
                this.#lifoItems.push(atOrThrow(items, index));
            }
            return;
        }
        this.#lifoItems.push(...items);
    }
}
//# sourceMappingURL=ordered-queue.js.map